import React, {createContext, useContext, useMemo} from 'react';

import {useViewAction} from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {type PanelBankFlowSectionConfig} from '../types';
import {
  useWorkspaceFlowLayout,
  useWorkspaceSectionPage,
} from './useWorkspaceFlowLayout';

export type WorkspaceSectionLayoutConfigsContextType = {
  columnsPerPage: number;
  currentPage: number;
  flowConfig: PanelBankFlowSectionConfig;
  initialRowsPerPage: number;
  maxNumSectionPages: number;
  maxRowsInSection: number;
  numTotalPanelsInSection: number;
  panelHeight: number;
  panelWidth: number;
  setCurrentPage: (page: number) => void;
  updateFlowConfig: (config: Partial<PanelBankFlowSectionConfig>) => void;
};

const WorkspaceSectionLayoutConfigsContext = createContext<
  WorkspaceSectionLayoutConfigsContextType | undefined
>(undefined);

type WorkspaceSectionLayoutConfigsProps = {
  children: React.ReactNode;
  numTotalPanelsInSection: number;
  sectionRef: PanelBankSectionConfigTypes.Ref;
};

export const WorkspaceSectionLayoutConfigs = ({
  children,
  numTotalPanelsInSection,
  sectionRef,
}: WorkspaceSectionLayoutConfigsProps) => {
  const updateFlowConfig = useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.updateFlowConfig
  );

  const {
    columnsPerPage,
    flowConfig,
    maxNumSectionPages,
    maxRowsInSection,
    panelHeight,
    panelWidth,
    rowsPerPage: initialRowsPerPage,
  } = useWorkspaceFlowLayout({
    panelBankSectionConfigRef: sectionRef,
    numTotalPanelsInSection,
  });

  const {currentPage, setCurrentPage} = useWorkspaceSectionPage({
    panelBankSectionConfigRef: sectionRef,
    maxNumSectionPages,
  });

  const value = useMemo(
    () => ({
      columnsPerPage,
      currentPage,
      flowConfig,
      initialRowsPerPage,
      maxNumSectionPages,
      maxRowsInSection,
      numTotalPanelsInSection,
      panelHeight,
      panelWidth,
      setCurrentPage,
      updateFlowConfig,
    }),
    [
      columnsPerPage,
      currentPage,
      flowConfig,
      initialRowsPerPage,
      maxNumSectionPages,
      maxRowsInSection,
      numTotalPanelsInSection,
      panelHeight,
      panelWidth,
      setCurrentPage,
      updateFlowConfig,
    ]
  );

  return (
    <WorkspaceSectionLayoutConfigsContext.Provider value={value}>
      {children}
    </WorkspaceSectionLayoutConfigsContext.Provider>
  );
};

export const useWorkspaceSectionLayoutConfigs =
  (): WorkspaceSectionLayoutConfigsContextType => {
    const value = useContext(WorkspaceSectionLayoutConfigsContext);

    if (value == null) {
      throw new Error(
        'useWorkspaceSectionLayoutConfigs must be used within a WorkspaceSectionLayoutConfigs'
      );
    }

    return value;
  };
